import { useState } from "react";
import { useProjectEvents, ProjectEvent } from "@/hooks/use-project-events";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { SrIcon } from "@/components/ui/SrIcon";
import { getInitials } from "@/components/shared/Avatar";
import {
  FILTER_OPTIONS,
  eventLabel,
  eventColor,
  eventDetail,
  type EventFilter,
} from "@/lib/event-registry";

interface ProjectLogTabProps {
  projectId: string;
}

function formatEventTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMin < 1) return "Nå";
  if (diffMin < 60) return `${diffMin}m siden`;
  if (diffHours < 24) return `${diffHours}t siden`;
  if (diffDays < 7) return `${diffDays}d siden`;

  return date.toLocaleDateString("nb-NO", {
    day: "2-digit",
    month: "short",
    year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
  });
}

function EventRow({ event }: { event: ProjectEvent }) {
  const badgeColor = eventColor(event.event_type);
  const label = eventLabel(event.event_type);
  const detail = eventDetail(event.event_type, event.payload);

  return (
    <div className="flex items-start gap-3 py-3 border-b last:border-b-0">
      <Avatar className="h-8 w-8 shrink-0 mt-0.5">
        <AvatarFallback className="text-xs bg-muted">
          {getInitials(event.user?.full_name)}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium">
            {event.user?.full_name ?? "System"}
          </span>
          <Badge variant="secondary" className={`text-xs ${badgeColor}`}>
            {label}
          </Badge>
        </div>
        {detail && (
          <p className="text-sm text-muted-foreground mt-0.5 truncate">
            {detail}
          </p>
        )}
      </div>
      <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">
        {formatEventTime(event.created_at)}
      </span>
    </div>
  );
}

export default function ProjectLogTab({ projectId }: ProjectLogTabProps) {
  const [filter, setFilter] = useState<EventFilter>("all");
  const [searchUser, setSearchUser] = useState("");
  const [page, setPage] = useState(0);

  const { data, isLoading } = useProjectEvents(projectId, filter, searchUser, page);
  const events = data?.events ?? [];
  const hasMore = data?.hasMore ?? false;

  return (
    <div className="space-y-4">
      {/* Filter chips */}
      <div className="flex flex-wrap gap-2">
        {FILTER_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => {
              setFilter(opt.value);
              setPage(0);
            }}
            className={`px-3 py-1 rounded-full text-sm transition-colors ${
              filter === opt.value
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:bg-muted/80"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <SrIcon name="search" className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"  />
        <Input
          placeholder="Søk på brukernavn..."
          value={searchUser}
          onChange={(e) => {
            setSearchUser(e.target.value);
            setPage(0);
          }}
          className="pl-9"
        />
      </div>

      {/* Event list */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <SrIcon name="loader2" className="h-6 w-6 animate-spin text-muted-foreground"  />
        </div>
      ) : events.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-sm">
          Ingen hendelser å vise
        </div>
      ) : (
        <div className="border rounded-lg px-4">
          {events.map((event) => (
            <EventRow key={event.id} event={event} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {(page > 0 || hasMore) && (
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
          >
            <SrIcon name="chevron-left" className="h-4 w-4 mr-1"  />
            Forrige
          </Button>
          <span className="text-sm text-muted-foreground">Side {page + 1}</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => p + 1)}
            disabled={!hasMore}
          >
            Neste
            <SrIcon name="chevron-right" className="h-4 w-4 ml-1"  />
          </Button>
        </div>
      )}
    </div>
  );
}
